/**
 * AuthContext.jsx - Scriptus Pharma Partner
 * Contexto de autenticação para farmácias
 * 
 * ✅ REFATORADO: Estrutura padronizada em PT-BR
 * ✅ REFATORADO: Helpers centralizados para verificação de plano/status
 * ✅ REFATORADO: Compatível com estrutura nova e legada
 * ✅ BUG FIX: Exporta objeto 'auth' do Firebase para uso em APIs
 * ✅ CENTRALIZADO: Helpers de verificação de conta (isContaArquivada, isContaBloqueada, isContaAtiva)
 * 
 * ESTRUTURA PADRONIZADA:
 * - plano: "basic" | "partner"
 * - statusAssinatura: "ativa" | "pendente" | "cancelada" | "inativa"
 * - stripe: { customerId, subscriptionId, account }
 * - assinatura: { iniciadaEm, proximaCobranca, canceladaEm, acessoAte, cancelaNoFimDoPeriodo }
 * - arquivamento: { dadosArquivados: boolean }
 * - bloqueada: boolean
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { 
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithCustomToken,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { api } from '../services/api';

// ============================================================================
// 🔧 HELPERS - Verificação de Estado da Conta
// ✅ CENTRALIZADOS: Usados por Login.jsx, Register.jsx e outros componentes
// ============================================================================

/**
 * Verifica se conta está arquivada (excluída permanentemente)
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {boolean}
 */
function isContaArquivada(pharmacy) {
  return pharmacy?.arquivamento?.dadosArquivados === true;
}

/**
 * Verifica se conta está bloqueada/desativada
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {boolean}
 */
function isContaBloqueada(pharmacy) {
  if (!pharmacy) return false;
  
  // Campo explícito de bloqueio
  if (pharmacy.bloqueada === true) return true;
  
  // Estrutura nova: statusAssinatura "inativa" SEM ser por cancelamento normal
  // Nota: "cancelada" e "pendente" NÃO são bloqueio
  if (pharmacy.statusAssinatura === 'inativa' && !pharmacy.assinatura?.acessoAte) {
    return true;
  }
  
  // Estrutura legada: ativo = false (mas não arquivada)
  if (pharmacy.ativo === false && !isContaArquivada(pharmacy)) {
    return true;
  }
  
  return false;
}

/**
 * Verifica se conta pode acessar (está ativa)
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {boolean}
 */
function isContaAtiva(pharmacy) {
  if (!pharmacy) return false;
  
  // Se arquivada ou bloqueada, não pode acessar
  if (isContaArquivada(pharmacy) || isContaBloqueada(pharmacy)) {
    return false;
  }
  
  // Estrutura nova: statusAssinatura
  const statusNovo = pharmacy.statusAssinatura;
  if (statusNovo) {
    // "ativa", "pendente" (aguardando pagamento Partner), "cancelada" (ainda no período) = pode acessar
    if (['ativa', 'active', 'pendente', 'pending', 'cancelada', 'cancelled'].includes(statusNovo)) {
      return true;
    }
  }
  
  // Estrutura legada: ativo = true
  if (pharmacy.ativo === true) return true;
  
  // Fallback: se não tem nenhum campo de status, considera ativa (conta nova)
  if (statusNovo === undefined && pharmacy.ativo === undefined) {
    return true;
  }
  
  return false;
}

// ============================================================================
// 🔧 HELPERS - Verificação de Plano e Assinatura
// ============================================================================

/**
 * Verifica se farmácia é Partner ativo
 * ✅ Compatível com estrutura nova e legada
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {boolean}
 */
function isPartnerAtivo(pharmacy) {
  if (!pharmacy) return false;
  
  // Verificar plano (novo: plano | legado: plan.type)
  const isPartnerPlan = (
    pharmacy.plano === 'partner' ||
    pharmacy.plano === 'pharma_partner' ||
    pharmacy.plan?.type === 'partner'
  );
  
  if (!isPartnerPlan) return false;
  
  // Verificar status (novo: statusAssinatura | legados: ativo, subscriptionStatus, assinatura.status)
  const isAtivo = (
    pharmacy.statusAssinatura === 'ativa' ||
    pharmacy.statusAssinatura === 'active' ||
    pharmacy.ativo === true ||
    pharmacy.subscriptionStatus === 'active' ||
    pharmacy.assinatura?.status === 'ativa' ||
    pharmacy.assinatura?.status === 'active'
  );
  
  return isAtivo;
}

/**
 * Verifica se assinatura está cancelada mas ainda no período
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {boolean}
 */
function isAssinaturaCanceladaMasAtiva(pharmacy) {
  if (!pharmacy) return false;
  
  const isCancelada = (
    pharmacy.statusAssinatura === 'cancelada' ||
    pharmacy.statusAssinatura === 'cancelled' ||
    pharmacy.assinatura?.status === 'cancelada'
  );
  
  if (!isCancelada) return false;
  
  // Verificar se ainda está no período de acesso
  const acessoAte = pharmacy.assinatura?.acessoAte || 
                    pharmacy.assinatura?.current_period_end ||
                    pharmacy.assinatura?.currentPeriodEnd;
  
  if (!acessoAte) return false;
  
  const dataAcesso = typeof acessoAte === 'number' 
    ? new Date(acessoAte * 1000) 
    : new Date(acessoAte);
  
  return new Date() < dataAcesso;
}

/**
 * Obtém tipo do plano
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {string} - 'basic' ou 'partner'
 */
function getTipoPlano(pharmacy) {
  if (!pharmacy) return 'basic';
  
  if (
    pharmacy.plano === 'partner' ||
    pharmacy.plano === 'pharma_partner' ||
    pharmacy.plan?.type === 'partner'
  ) {
    return 'partner';
  }
  
  return 'basic';
}

/**
 * Obtém status da assinatura em formato padronizado
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {string} - 'ativa', 'pendente', 'cancelada', 'inativa'
 */
function getStatusAssinatura(pharmacy) {
  if (!pharmacy) return 'inativa';
  
  // Priorizar campo novo
  if (pharmacy.statusAssinatura) {
    // Normalizar valores em inglês para português
    const statusMap = {
      'active': 'ativa',
      'pending': 'pendente',
      'cancelled': 'cancelada',
      'canceled': 'cancelada',
      'inactive': 'inativa'
    };
    return statusMap[pharmacy.statusAssinatura] || pharmacy.statusAssinatura;
  }
  
  // Fallback para campos legados
  if (pharmacy.ativo === true) return 'ativa';
  if (pharmacy.subscriptionStatus === 'active') return 'ativa';
  if (pharmacy.assinatura?.status) {
    const statusMap = {
      'active': 'ativa',
      'ativa': 'ativa',
      'cancelada': 'cancelada',
      'cancelled': 'cancelada'
    };
    return statusMap[pharmacy.assinatura.status] || 'inativa';
  }
  
  return 'inativa';
}

/**
 * Obtém limite de dispensações baseado no plano
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {number} - Limite (-1 = ilimitado)
 */
function getLimiteDispensacoes(pharmacy) {
  if (!pharmacy) return 50;
  
  // Campo novo
  if (pharmacy.limiteDispensacoes !== undefined) {
    return pharmacy.limiteDispensacoes;
  }
  
  // Campo legado
  if (pharmacy.dispensationsLimit !== undefined) {
    return pharmacy.dispensationsLimit;
  }
  
  // Baseado no plano
  return isPartnerAtivo(pharmacy) ? -1 : 50;
}

/**
 * Obtém dispensações do mês atual
 * @param {Object} pharmacy - Dados da farmácia
 * @returns {number}
 */
function getDispensacoesNoMes(pharmacy) {
  if (!pharmacy) return 0;
  
  return pharmacy.dispensacoesNoMes || pharmacy.dispensationsThisMonth || 0;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

// ============================================================================
// PROVIDER
// ============================================================================

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [pharmacy, setPharmacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ———————————————————————————————————————————————————————————————————————————
  // LISTENER DE AUTENTICAÇÃO
  // ———————————————————————————————————————————————————————————————————————————
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // Busca dados da farmácia no Firestore
          const pharmacyDoc = await getDoc(doc(db, 'pharmacies', firebaseUser.uid));
          
          if (pharmacyDoc.exists()) {
            const pharmacyData = pharmacyDoc.data();
            
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName
            });
            
            setPharmacy(pharmacyData);
          } else {
            // Usuário autenticado mas não é farmácia
            console.warn('Usuário não é uma farmácia cadastrada');
            await firebaseSignOut(auth);
            setUser(null);
            setPharmacy(null);
          }
        } else {
          setUser(null);
          setPharmacy(null);
        }
      } catch (err) {
        console.error('Erro ao carregar dados do usuário:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ———————————————————————————————————————————————————————————————————————————
  // REGISTRO DE NOVA FARMÁCIA
  // ———————————————————————————————————————————————————————————————————————————
  const register = useCallback(async (email, password, companyData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Rota da API de registro
      const response = await api.post('/api/v1/auth/pharmacy/register', {
        email,
        password,
        companyData
      });
      
      // Backend retorna "customToken" ou "token"
      const customToken = response.data.customToken || response.data.token;
      
      // Capturar uid do response primeiro (mais confiável)
      let uid = response.data.uid;
      let idToken = null;
      
      if (customToken) {
        // Capturar userCredential para evitar race condition
        const userCredential = await signInWithCustomToken(auth, customToken);
        
        // Usar userCredential.user ao invés de auth.currentUser
        idToken = await userCredential.user.getIdToken();
        
        // Fallback: se uid não veio do backend, pegar do userCredential
        if (!uid) {
          uid = userCredential.user.uid;
        }
      }
      
      // Validação final
      if (!uid) {
        throw new Error('UID não retornado pelo servidor');
      }
      
      // ✅ Retornar com campos padronizados
      return { 
        success: true, 
        data: response.data,
        uid: uid,
        token: idToken,
        plano: response.data.plano || 'basic',
        requerPagamento: response.data.requerPagamento || false
      };
      
    } catch (err) {
      console.error('Erro no registro:', err);
      
      const errorMessage = err.response?.data?.error?.message || err.message || 'Erro ao cadastrar';
      const errorCode = err.response?.data?.error?.code || err.code || 'desconhecido';
      
      setError(errorMessage);
      return { success: false, error: errorMessage, code: errorCode };
    } finally {
      setLoading(false);
    }
  }, []);

  // ———————————————————————————————————————————————————————————————————————————
  // LOGIN
  // ———————————————————————————————————————————————————————————————————————————
  const signIn = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Verifica se é farmácia
      const pharmacyDoc = await getDoc(doc(db, 'pharmacies', userCredential.user.uid));
      
      if (!pharmacyDoc.exists()) {
        await firebaseSignOut(auth);
        const customError = new Error('Esta conta não é uma farmácia cadastrada');
        customError.code = 'auth/nao-e-farmacia';
        throw customError;
      }
      
      return { success: true };
      
    } catch (err) {
      let errorMessage = 'Erro ao fazer login';
      const errorCode = err.code || 'desconhecido';
      
      // Mapear erros para português
      const errorMap = {
        'auth/user-not-found': 'Usuário não encontrado',
        'auth/wrong-password': 'Senha incorreta',
        'auth/invalid-email': 'Email inválido',
        'auth/invalid-credential': 'Email ou senha incorretos',
        'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
        'auth/user-disabled': 'Esta conta foi desativada.',
        'auth/network-request-failed': 'Erro de conexão. Verifique sua internet.',
        'auth/nao-e-farmacia': 'Esta conta não é uma farmácia cadastrada'
      };
      
      errorMessage = errorMap[err.code] || err.message;
      
      setError(errorMessage);
      return { success: false, error: errorMessage, code: errorCode };
    } finally {
      setLoading(false);
    }
  }, []);

  // ———————————————————————————————————————————————————————————————————————————
  // LOGOUT
  // ———————————————————————————————————————————————————————————————————————————
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setPharmacy(null);
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
    }
  }, []);

  // ———————————————————————————————————————————————————————————————————————————
  // RECUPERAÇÃO DE SENHA
  // ———————————————————————————————————————————————————————————————————————————
  const resetPassword = useCallback(async (email) => {
    setLoading(true);
    setError(null);
    
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      const errorMap = {
        'auth/user-not-found': 'Email não cadastrado',
        'auth/invalid-email': 'Email inválido',
        'auth/too-many-requests': 'Muitas tentativas. Aguarde alguns minutos.'
      };
      
      const errorMessage = errorMap[err.code] || err.message || 'Erro ao enviar email de recuperação';
      const errorCode = err.code || 'desconhecido';
      
      setError(errorMessage);
      
      // Lança o erro para o componente tratar
      const customError = new Error(errorMessage);
      customError.code = errorCode;
      throw customError;
    } finally {
      setLoading(false);
    }
  }, []);

  // ———————————————————————————————————————————————————————————————————————————
  // ATUALIZAR DADOS DA FARMÁCIA
  // ———————————————————————————————————————————————————————————————————————————
  const refreshPharmacyData = useCallback(async () => {
    if (!user) return;
    
    try {
      const pharmacyDoc = await getDoc(doc(db, 'pharmacies', user.uid));
      if (pharmacyDoc.exists()) {
        setPharmacy(pharmacyDoc.data());
      }
    } catch (err) {
      console.error('Erro ao atualizar dados da farmácia:', err);
    }
  }, [user]);

  // ———————————————————————————————————————————————————————————————————————————
  // OBTER TOKEN DE AUTENTICAÇÃO
  // ———————————————————————————————————————————————————————————————————————————
  const getAuthToken = useCallback(async () => {
    if (!auth.currentUser) return null;
    return await auth.currentUser.getIdToken();
  }, []);

  // ———————————————————————————————————————————————————————————————————————————
  // LIMPAR ERRO
  // ———————————————————————————————————————————————————————————————————————————
  const clearError = useCallback(() => setError(null), []);

  // ———————————————————————————————————————————————————————————————————————————
  // VALORES COMPUTADOS (MEMOIZADOS)
  // ———————————————————————————————————————————————————————————————————————————
  const computedValues = useMemo(() => ({
    // Status de autenticação
    isAuthenticated: !!user,
    
    // ✅ Verificações de plano usando helpers padronizados
    isPartner: isPartnerAtivo(pharmacy),
    isPremium: isPartnerAtivo(pharmacy), // Alias para compatibilidade
    
    // ✅ Tipo do plano
    tipoPlano: getTipoPlano(pharmacy),
    
    // ✅ Status da assinatura
    statusAssinatura: getStatusAssinatura(pharmacy),
    
    // ✅ Verificação de cancelamento
    isCanceladaMasAtiva: isAssinaturaCanceladaMasAtiva(pharmacy),
    
    // ✅ Verificações de estado da conta
    isContaArquivada: isContaArquivada(pharmacy),
    isContaBloqueada: isContaBloqueada(pharmacy),
    isContaAtiva: isContaAtiva(pharmacy),
    
    // ✅ Limites de dispensação
    limiteDispensacoes: getLimiteDispensacoes(pharmacy),
    dispensacoesNoMes: getDispensacoesNoMes(pharmacy),
    
    // ✅ Verificar se pode dispensar
    podeDispensar: () => {
      const limite = getLimiteDispensacoes(pharmacy);
      if (limite === -1) return true; // Ilimitado
      return getDispensacoesNoMes(pharmacy) < limite;
    },
    
    // ✅ Dispensações restantes
    dispensacoesRestantes: () => {
      const limite = getLimiteDispensacoes(pharmacy);
      if (limite === -1) return Infinity;
      return Math.max(0, limite - getDispensacoesNoMes(pharmacy));
    }
  }), [pharmacy, user]);

  // ———————————————————————————————————————————————————————————————————————————
  // VALUE DO CONTEXT
  // ———————————————————————————————————————————————————————————————————————————
  const value = useMemo(() => ({
    // Estado
    user,
    pharmacy,
    loading,
    error,
    
    // ✅ BUG FIX: Exportar objeto auth do Firebase para uso em APIs
    auth,
    
    // Valores computados
    ...computedValues,
    
    // Funções de autenticação
    register,
    signIn,
    signOut,
    resetPassword,
    
    // Funções auxiliares
    refreshPharmacyData,
    getAuthToken,
    clearError,
    
    // ✅ Helpers exportados para uso em outros componentes
    helpers: {
      // Verificação de estado da conta
      isContaArquivada,
      isContaBloqueada,
      isContaAtiva,
      // Verificação de plano/assinatura
      isPartnerAtivo,
      isAssinaturaCanceladaMasAtiva,
      getTipoPlano,
      getStatusAssinatura,
      getLimiteDispensacoes,
      getDispensacoesNoMes
    }
  }), [
    user,
    pharmacy,
    loading,
    error,
    computedValues,
    register,
    signIn,
    signOut,
    resetPassword,
    refreshPharmacyData,
    getAuthToken,
    clearError
    // Nota: 'auth' não precisa estar nas deps pois é importado e não muda
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

// Exportar helpers para uso direto em outros arquivos
export {
  // Verificação de estado da conta
  isContaArquivada,
  isContaBloqueada,
  isContaAtiva,
  // Verificação de plano/assinatura
  isPartnerAtivo,
  isAssinaturaCanceladaMasAtiva,
  getTipoPlano,
  getStatusAssinatura,
  getLimiteDispensacoes,
  getDispensacoesNoMes
};